import { useEffect, useRef, type Ref } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useField } from "formik";
import DOMPurify from "dompurify";
import { GenericField } from "@/lib/Components/Form/Fields/GenericField";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  proDriverAgreementMergeFieldsQuery,
  ProDriverAgreementMergeField,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementTemplateQueries";
import { Button } from "@/components/catalyst/button";
import {
  Dropdown,
  DropdownButton,
  DropdownHeading,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection,
} from "@/components/catalyst/dropdown";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import {
  BoldIcon,
  ItalicIcon,
  Heading1Icon,
  Heading2Icon,
  ListIcon,
  ListOrderedIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { MergeTagHighlight } from "@/app/ProDrivers/Agreements/Utils/mergeTagHighlight";

type Props = {
  name: string;
  label: string;
  initialValue?: string;
  heightClass?: string;
  /** Stretch to the parent's height; the pane supplies the label. */
  fill?: boolean;
  /** Scroll container of the editor, used to sync scrolling with the preview. */
  scrollRef?: Ref<HTMLDivElement>;
  /** Off for a real agreement — its merge fields were filled in on creation. */
  showMergeFields?: boolean;
};

export function AgreementBodyInput({
  name,
  label,
  initialValue,
  heightClass,
  fill,
  scrollRef,
  showMergeFields = true,
}: Props) {
  const [field, , { setValue }] = useField(name);
  const initialHtml = initialValue ?? field.value ?? "";
  // Last value this editor wrote — sanitising rewrites the markup, so without
  // it the sync below reads our own change as an outside one.
  const lastSeenRef = useRef<string>(initialHtml);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: "Write the agreement wording…" }),
      MergeTagHighlight,
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class:
          "prose prose-zinc min-h-[280px] max-w-none focus:outline-none px-4 py-3",
      },
    },
    onUpdate: ({ editor }) => {
      const html = DOMPurify.sanitize(editor.getHTML());
      lastSeenRef.current = html;
      setValue(html);
    },
  });

  // Sync content in when the form value changes from outside the editor.
  useEffect(() => {
    if (!editor) return;

    const value = field.value ?? "";
    if (value !== lastSeenRef.current && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
      lastSeenRef.current = value;
    }
  }, [editor, field.value]);

  const editorBox = (
    <div
      className={cn(
        "flex flex-col rounded-md border border-gray-300 bg-white",
        heightClass,
      )}
    >
      {editor ? (
        <Toolbar editor={editor} showMergeFields={showMergeFields} />
      ) : null}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );

  // GenericField's own wrapper can't stretch, so a filling editor skips it and
  // relies on the surrounding pane for its label.
  if (fill) {
    return editorBox;
  }

  return (
    <GenericField
      name={name}
      label={label}
      className="col-span-full"
      viewNode={
        <div
          className="prose prose-zinc max-w-none"
          dangerouslySetInnerHTML={{
            __html: field.value ? DOMPurify.sanitize(field.value) : "--",
          }}
        />
      }
    >
      {editorBox}
    </GenericField>
  );
}

function Toolbar({
  editor,
  showMergeFields,
}: {
  editor: Editor;
  showMergeFields: boolean;
}) {
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50/80 px-2 py-1 backdrop-blur">
      <ToolbarButton
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        ariaLabel="Bold"
      >
        <BoldIcon className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        ariaLabel="Italic"
      >
        <ItalicIcon className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("heading", { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        ariaLabel="Heading 1"
      >
        <Heading1Icon className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        ariaLabel="Heading 2"
      >
        <Heading2Icon className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        ariaLabel="Bullet list"
      >
        <ListIcon className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        ariaLabel="Numbered list"
      >
        <ListOrderedIcon className="size-4" />
      </ToolbarButton>

      {showMergeFields ? (
        <>
          <div className="mx-1 h-5 w-px bg-gray-200" />

          <MergeFieldPicker editor={editor} />
        </>
      ) : null}
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  ariaLabel,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        "rounded p-1.5 text-gray-600 hover:bg-gray-200",
        active && "bg-gray-200 text-gray-900",
      )}
    >
      {children}
    </button>
  );
}

function MergeFieldPicker({ editor }: { editor: Editor }) {
  const { data } = useGqlQuery(proDriverAgreementMergeFieldsQuery, {});
  const fields = data?.proDriverAgreementMergeFields ?? [];

  const groups = fields.reduce<Record<string, ProDriverAgreementMergeField[]>>(
    (acc, field) => {
      acc[field.group] = [...(acc[field.group] ?? []), field];
      return acc;
    },
    {},
  );

  return (
    <Dropdown>
      <DropdownButton as={Button} plain>
        Insert detail
        <ChevronDownIcon className="size-4" />
      </DropdownButton>
      <DropdownMenu className="max-h-96 overflow-auto">
        {Object.entries(groups).map(([group, groupFields]) => (
          <DropdownSection key={group} aria-label={group}>
            <DropdownHeading>{group}</DropdownHeading>
            {groupFields.map((field) => (
              <DropdownItem
                key={field.token}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .insertContent(`{{${field.token}}}`)
                    .run()
                }
              >
                <DropdownLabel>{field.label}</DropdownLabel>
              </DropdownItem>
            ))}
          </DropdownSection>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
